import * as actionTypes from "../constants/actionTypes";
import * as priceTypes from "../constants/priceTypes";
import * as settings from "../constants/settings";
import * as modelHelper from "../helpers/modelHelper";
import * as getterHelper from "../helpers/getterHelper";

export const initialState = {
    primaryCurrency: modelHelper.mapDefaultCurrencyDataToObject(),
    mainCurrency: modelHelper.mapDefaultCurrencyDataToObject(),
    priceDefinition: settings.PURCHASE_ONLY,
    priceUnitDefinition: settings.UNIT_L_ID,
    priceGroupNumber: 0,
    priceGroupsEnabled: false,
    showLocalPrices: false,
    showMargin: true,
    showMarkup: true,
    showDiscount: true,
    currencyList: [],
    customCurrencyList: [],
    genericPriceGroups: [],
    genericColorantPosCoeficient: null,
    genericColorantPurchase: null,
    genericColorantSell: null,
    genericColorantVat: null,
    genericProductPosCoeficient: null,
    genericProductPurchase: null,
    genericProductSell: null,
    genericProductVat: null,
    genericPackageSurcharge: null,
    colorantData: [],
    baseInProductsData: [],
    baseInProductPackagesData: [],
    packageData: [],
    activeProduct: null
};

/**
 * Structure of pricing store:
 * - primaryCurrency {Object}
 * - mainCurrency {Object}
 * - priceDefinition {String}                   - definition of pricing logic
 * - priceUnitDefinition {String}               - definition of unit for price settings
 * - priceGroupNumber {Number}
 * - priceGroupsEnabled {Boolean}
 * - showLocalPrices {Boolean}
 * - showMargin {Boolean}
 * - showMarkup {Boolean}
 * - showDiscount {Boolean}
 * - currencyList {Array}                       - array of default currencies
 * - customCurrencyList {Array}                 - array of custom currencies
 * - genericPriceGroups {Array}
 * - genericColorantPosCoeficient {Array}
 * - genericColorantPurchase {Object}
 * - genericColorantSell {Object}
 * - genericColorantVat {Object}
 * - genericProductPosCoeficient {Object}
 * - genericProductPurchase {Object}
 * - genericProductSell {Object}
 * - genericProductVat {Object}
 * - genericPackageSurcharge {Object}
 * - colorantData {Array}
 * - baseInProductsData {Array}
 * - baseInProductPackagesData {Array}
 * - packageData {Array}
 * - activeProduct {Object}
 */
export const pricingReducer = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.LOG_OUT_SUCCESS:
        case actionTypes.WEBSOCKET_CLOSED:
        case actionTypes.DATABASE_CHANGE:
            return initialState;
        case actionTypes.SAVE_PRIMARY_CURRENCY:
            let primaryCurrency = modelHelper.mapCurrencyToObject(action.payload.currency);

            window.storeProvider.setPrimaryCurrencyCode(primaryCurrency.code);

            return {
                ...state,
                primaryCurrency: primaryCurrency
            };
        case actionTypes.LOG_IN_SUCCESS:
        case actionTypes.CHANGE_USER_SUCCESS:
            let userCurrency = modelHelper.mapCurrencyToObject(action.payload.loggedUser.currency);

            window.storeProvider.setCurrencyData(userCurrency.code, userCurrency.symbol);
            window.storeProvider.setPriceRounding(userCurrency.rounding);

            return {
                ...state,
                mainCurrency: userCurrency
            };
        case actionTypes.SAVE_ACTIVE_CURRENCY:
            let newUserCurrency = modelHelper.mapCurrencyToObject(action.payload.currency);

            window.storeProvider.setCurrencyData(newUserCurrency.code, newUserCurrency.symbol);
            window.storeProvider.setPriceRounding(newUserCurrency.rounding);

            return {
                ...state,
                mainCurrency: newUserCurrency
            };
        case actionTypes.INIT_PRICE_SETTINGS:
        case actionTypes.SAVE_PRICE_SETTINGS:
            return {
                ...state,
                priceDefinition: action.payload.priceDefinition,
                priceUnitDefinition: action.payload.priceUnitDefinition,
                priceGroupNumber: action.payload.priceGroupNumber,
                priceGroupsEnabled: action.payload.priceGroupsEnabled,
                showLocalPrices: action.payload.showLocalPrices,
                showMargin: action.payload.showMargin,
                showMarkup: action.payload.showMarkup,
                showDiscount: action.payload.showDiscount
            };
        case actionTypes.SAVE_CURRENCY_LIST:
            return {
                ...state,
                currencyList: modelHelper.mapCurrenciesToObjects(action.payload.currencyList.default),
                customCurrencyList: modelHelper.mapCurrenciesToObjects(action.payload.currencyList.custom)
            };
        case actionTypes.SAVE_PRICING_GENERIC_DATA:
            let genericData = modelHelper.mapPriceDataToObjects(action.payload.genericData, state.mainCurrency.rate);

            let numberOfGroups = getterHelper.getPriceObjectByType(genericData, priceTypes.NUMBER_OF_PRICE_GROUPS);

            if (numberOfGroups === null || numberOfGroups.value === 0) {
                return state;
            }

            let groups = [];
            for (let item of genericData) {
                if (item.priceType === priceTypes.PRICE_GROUP_TYPE) {
                    groups.push(item);
                }
            }

            return {
                ...state,
                genericPriceGroups: groups
            };
        case actionTypes.SAVE_PRICING_GENERIC_COLORANT_DATA:
            let genericColorantData = modelHelper.mapPriceDataToObjects(
                action.payload.genericColorantData,
                state.mainCurrency.rate
            );

            return {
                ...state,
                genericColorantPosCoeficient: getterHelper.getPriceObjectByType(
                    genericColorantData,
                    priceTypes.POS_COEFICIENT
                ),
                genericColorantPurchase: getterHelper.getPriceObjectByType(
                    genericColorantData,
                    priceTypes.PURCHASE_PER_LITER
                ),
                genericColorantSell: getterHelper.getPriceObjectByType(genericColorantData, priceTypes.SELL_PER_LITER),
                genericColorantVat: getterHelper.getPriceObjectByType(genericColorantData, priceTypes.VAT)
            };
        case actionTypes.SAVE_PRICING_GENERIC_PRODUCT_DATA:
            let genericProductData = modelHelper.mapPriceDataToObjects(
                action.payload.genericProductData,
                state.mainCurrency.rate
            );

            return {
                ...state,
                genericProductPosCoeficient: getterHelper.getPriceObjectByType(
                    genericProductData,
                    priceTypes.POS_COEFICIENT
                ),
                genericProductPurchase: getterHelper.getPriceObjectByType(
                    genericProductData,
                    priceTypes.PURCHASE_PER_LITER
                ),
                genericProductSell: getterHelper.getPriceObjectByType(genericProductData, priceTypes.SELL_PER_LITER),
                genericProductVat: getterHelper.getPriceObjectByType(genericProductData, priceTypes.VAT)
            };
        case actionTypes.SAVE_PRICING_GENERIC_PACKAGE_DATA:
            return {
                ...state,
                genericPackageSurcharge: getterHelper.getPriceObjectByType(
                    modelHelper.mapPriceDataToObjects(action.payload.genericPackageData, state.mainCurrency.rate),
                    priceTypes.SURCHARGE
                )
            };
        case actionTypes.SAVE_PRICING_COLORANT_DATA:
            return {
                ...state,
                colorantData: modelHelper.mapColorantPriceDataToObjects(
                    action.payload.colorantData,
                    state.mainCurrency.rate,
                    state.priceUnitDefinition
                )
            };
        case actionTypes.SAVE_PRICING_BASE_IN_PRODUCTS_DATA:
            return {
                ...state,
                baseInProductsData: modelHelper.mapBaseInProductsPriceDataToObjects(
                    action.payload.baseInProductsData,
                    state.mainCurrency.rate,
                    state.priceUnitDefinition
                )
            };
        case actionTypes.SAVE_PRICING_PRODUCT_DATA:
            return {
                ...state,
                baseInProductPackagesData: modelHelper.mapBaseInProductPackagesPriceDataToObjects(
                    action.payload.baseInProductPackagesData,
                    state.mainCurrency.rate
                )
            };
        case actionTypes.SAVE_PRICING_ACTIVE_PRODUCT:
            return {
                ...state,
                activeProduct: action.payload.product,
                baseInProductsData: [],
                baseInProductPackagesData: []
            };
        case actionTypes.SAVE_PRICING_PACKAGE_DATA:
            return {
                ...state,
                packageData: modelHelper.mapPackagePriceDataToObjects(
                    action.payload.packageData,
                    state.mainCurrency.rate
                )
            };
        case actionTypes.PRICE_SETTINGS_SAVE_PRICE_DEFINITION:
            return {
                ...state,
                priceDefinition: action.payload.priceDefinition
            };
        case actionTypes.PRICE_SETTINGS_SAVE_PRICE_UNIT_DEFINITION:
            return {
                ...state,
                priceUnitDefinition: action.payload.priceUnitDefinition
            };
        case actionTypes.PRICE_SETTINGS_SAVE_PRICE_GROUP_NUMBER:
            return {
                ...state,
                priceGroupNumber: action.payload.priceGroupNumber
            };
        case actionTypes.PRICE_SETTINGS_SAVE_PRICE_GROUPS:
            return {
                ...state,
                priceGroupsEnabled: action.payload.priceGroupsEnabled
            };
        case actionTypes.PRICE_SETTINGS_SAVE_LOCAL_PRICES:
            return {
                ...state,
                showLocalPrices: action.payload.showLocalPrices
            };
        case actionTypes.PRICE_SETTINGS_SAVE_MARGIN:
            return {
                ...state,
                showMargin: action.payload.showMargin
            };
        case actionTypes.PRICE_SETTINGS_SAVE_MARKUP:
            return {
                ...state,
                showMarkup: action.payload.showMarkup
            };
        case actionTypes.PRICE_SETTINGS_SAVE_DISCOUNT:
            return {
                ...state,
                showDiscount: action.payload.showDiscount
            };
        default:
            return state;
    }
};
